"use client";

import { startTransition, useCallback, useEffect, useRef } from "react";
import { type Cart as CartData } from "@cappelendamm/e-commerce-services";
import { useCartBroadcast } from "../useCartBroadcast";
import { UNINITIALIZED_CART } from "../initial-cart";

type DispatchAction = (action: {
  type: "SET_CART";
  cartData: CartData;
}) => void;

/**
 * Synchronizes cart state across browser tabs.
 * Listens for cart updates from other tabs and broadcasts local changes.
 */
export function CartTabSync({
  cartData,
  dispatchAction,
}: {
  cartData: CartData;
  dispatchAction: DispatchAction;
}) {
  const lastReceivedCartUpdatedAt = useRef<string>("");
  const lastBroadcastedCartUpdatedAt = useRef<string>("");

  const onBroadcastReceived = useCallback(
    (cart: CartData) => {
      if (cart.id === UNINITIALIZED_CART.id) {
        return;
      }
      lastReceivedCartUpdatedAt.current = cart.updatedAt;
      startTransition(async () => {
        dispatchAction({ type: "SET_CART", cartData: cart });
      });
    },
    [dispatchAction],
  );

  const { broadcast } = useCartBroadcast(onBroadcastReceived);

  useEffect(() => {
    if (
      cartData.updatedAt !== lastBroadcastedCartUpdatedAt.current &&
      cartData.updatedAt !== lastReceivedCartUpdatedAt.current
    ) {
      lastBroadcastedCartUpdatedAt.current = cartData.updatedAt;
      broadcast(cartData);
    }
  }, [broadcast, cartData]);

  return null;
}
